import React, { useState, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { Text } from '@/components/common/text';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '../GlassCard';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { commonErrorMessages } from '@/screens/residents/faq/faq';

const ICON_SIZE = 24;

export const FAQErrorTable: React.FC = React.memo(() => {
  const { colors } = useThemeColors();
  const space = useSpacing();
  const [expanded, setExpanded] = useState(false);

  const toggle = useCallback(() => setExpanded((prev) => !prev), []);

  return (
    <View style={styles.container}>
      <Hoverable
        hover="row"
        bleed={{ x: space.sm, y: space.sm }}
        style={styles.header}
        accessibilityRole="button"
        onPress={toggle}
        testID={`FAQ_CommonErrors_${expanded ? 'collapse' : 'expand'}`}
      >
        <Text style={[styles.title, { color: colors.text.primary }]}>Common Error Messages</Text>
        {expanded ? (
          <RESIDENTS_SVG_COMPONENTS.CIRCLE_MINUS width={ICON_SIZE} height={ICON_SIZE} color={colors.text.muted} />
        ) : (
          <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={ICON_SIZE} height={ICON_SIZE} color={colors.text.muted} />
        )}
      </Hoverable>
      {expanded && (
        <GlassCard borderRadius={12} padding={0} style={styles.tableCard}>
          {commonErrorMessages.map((row, idx) => {
            const isHeader = idx === 0;
            const isLast = idx === commonErrorMessages.length - 1;
            return (
              <View
                key={idx}
                style={[styles.row, !isLast && { borderBottomWidth: 1, borderBottomColor: colors.separator }]}
              >
                <Text
                  style={[
                    styles.cell,
                    styles.cellCode,
                    { color: colors.text.subtle },
                    isHeader && { ...REDESIGN_TYPOGRAPHY.cardTitle, color: colors.text.primary },
                  ]}
                >
                  {row[0]}
                </Text>
                <View style={[styles.cellDivider, { backgroundColor: colors.separator }]} />
                <Text
                  style={[
                    styles.cell,
                    styles.cellAction,
                    { color: colors.text.subtle },
                    isHeader && { ...REDESIGN_TYPOGRAPHY.cardTitle, color: colors.text.primary },
                  ]}
                >
                  {row[1]}
                </Text>
              </View>
            );
          })}
        </GlassCard>
      )}
    </View>
  );
});

FAQErrorTable.displayName = 'FAQErrorTable';

const styles = StyleSheet.create({
  container: {
    paddingVertical: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    flex: 1,
  },
  tableCard: {
    marginTop: 12,
  },
  row: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  cell: {
    ...REDESIGN_TYPOGRAPHY.small,
  },
  cellCode: {
    flex: 2,
    paddingRight: 8,
  },
  cellDivider: {
    width: 1,
    marginHorizontal: 8,
  },
  cellAction: {
    flex: 3,
  },
});
